import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { z } from "zod";

import { AuthShell } from "@/components/shared/AuthShell";
import { PasswordStrengthIndicator } from "@/components/shared/PasswordStrengthIndicator";
import {
  getLandingPrivacyUrl,
  getLandingTermsUrl,
} from "@/lib/landingLinks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useAuth } from "@/contexts/AuthContext";
import { apiBaseUrl } from "@/services/apiClient";
import {
  sendVerificationEmail as sendVerificationEmailApi,
  verifyEmail as verifyEmailApi,
} from "@/services/authService";
import { fireRegistrationConversion } from "@/lib/google-ads";
import { TURNSTILE_SITE_KEY, turnstileEnabled } from "@/lib/turnstile";

type Step = 1 | 2;

function getApiErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: { message?: unknown } } }).response
      ?.data;
    const m = data?.message;
    if (typeof m === "string") return m;
    if (Array.isArray(m)) return m.join(", ");
  }
  return undefined;
}

export function RegisterPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isLoading, registerAndLogin } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance | null>(null);

  const returnTo = searchParams.get("returnTo") ?? "/";

  const handleTurnstileSuccess = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
  }, []);

  const resetTurnstile = useCallback(() => {
    setTurnstileToken(null);
    turnstileRef.current?.reset();
  }, []);

  const formSchema = z
    .object({
      firstName: z.string().min(1, t("validation.required")),
      lastName: z.string().min(1, t("validation.required")),
      email: z
        .string()
        .min(1, t("validation.emailRequired"))
        .email(t("validation.emailInvalid")),
      password: z
        .string()
        .min(8, t("auth.register.passwordStrength.minLength")),
      confirmPassword: z.string().min(1, t("auth.register.confirmPasswordRequired")),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth.register.passwordMismatch"),
      path: ["confirmPassword"],
    });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const handleSendCode = async (values: FormValues): Promise<void> => {
    setEmail(values.email);
    setPassword(values.password);
    setSendingCode(true);
    try {
      await sendVerificationEmailApi(values.email, i18n.language);
      setStep(2);
      setResendCooldown(10);
    } catch (err: unknown) {
      const status = err && typeof err === "object" && "response" in err
        ? (err as { response?: { status?: number } }).response?.status
        : undefined;
      if (status === 409) {
        toast.error(t("auth.register.emailAlreadyRegisteredError"));
      } else {
        toast.error(t("auth.register.error"));
      }
    } finally {
      setSendingCode(false);
    }
  };

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [resendCooldown]);

  const handleResendCode = async (): Promise<void> => {
    if (resendCooldown > 0 || resendingCode || email === "") return;
    setResendingCode(true);
    try {
      await sendVerificationEmailApi(email, i18n.language);
      setResendCooldown(10);
      toast.success(t("auth.register.resendCodeSuccess"));
    } catch {
      toast.error(t("auth.register.resendCodeError"));
    } finally {
      setResendingCode(false);
    }
  };

  const handleVerifyAndRegister = async (): Promise<void> => {
    const code = verificationCode.trim();
    if (code.length !== 6) {
      toast.error(t("auth.register.invalidCodeError"));
      return;
    }
    if (turnstileEnabled && (turnstileToken == null || turnstileToken === "")) {
      toast.error(t("auth.register.registrationCaptchaRequired"));
      return;
    }
    setVerifying(true);
    try {
      const { registrationToken } = await verifyEmailApi(email, code);
      const { firstName, lastName } = form.getValues();
      try {
        await registerAndLogin(
          email,
          password,
          registrationToken,
          { firstName, lastName },
          turnstileEnabled ? turnstileToken ?? undefined : undefined,
        );
        toast.success(t("auth.register.successToast"));
        fireRegistrationConversion();
        // New consumers are auto-created as `person` server-side now, so
        // the home redirect just works — no post-signup picker to bounce
        // through. The name-entry gate in ProtectedRoute still fires if
        // the user somehow lacks a first/last name.
        navigate("/", { replace: true });
      } catch (regErr: unknown) {
        const msg = getApiErrorMessage(regErr)?.toLowerCase() ?? "";
        if (msg.includes("captcha")) {
          toast.error(t("auth.register.captchaFailed"));
          resetTurnstile();
          return;
        }
        toast.error(t("auth.register.error"));
      }
    } catch {
      toast.error(t("auth.register.invalidCodeError"));
    } finally {
      setVerifying(false);
    }
  };

  const startGoogleSso = useCallback((): void => {
    setSsoLoading(true);
    const ssoUrl =
      returnTo && returnTo !== "/"
        ? `${apiBaseUrl}/auth/sso/google?returnTo=${encodeURIComponent(returnTo)}`
        : `${apiBaseUrl}/auth/sso/google`;
    window.location.href = ssoUrl;
  }, [returnTo]);

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-auth-surface" />
    );
  }
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthShell
      /* Signup is CONSUMER-ONLY: professionals are invitation-only and never
         reach this page (decision e7984b6), so the cover speaks to one
         audience and carries no role preview. */
      cover={{
        title: t("auth.login.cover.userTitle"),
        body: t("auth.login.cover.userBody"),
      }}
      footer={
        <Trans
          i18nKey="auth.register.legalAgreement"
          components={{
            terms: (
              <a
                href={getLandingTermsUrl(i18n.resolvedLanguage ?? i18n.language)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              />
            ),
            privacy: (
              <a
                href={getLandingPrivacyUrl(i18n.resolvedLanguage ?? i18n.language)}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
              />
            ),
          }}
        />
      }
    >
      <div className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                      <h1 className="text-2xl font-bold text-foreground">
                        {t("auth.register.title")}
                      </h1>
                      <p className="text-balance text-muted-foreground">
                        {step === 1
                          ? t("auth.register.wizard.credentialsSubtitle")
                          : t("auth.register.step3Title")}
                      </p>
                    </div>

                    {step === 1 ? (
                      <>
                        <GoogleSsoButton
                          loading={ssoLoading}
                          onClick={startGoogleSso}
                          label={t("auth.sso.signUpWithGoogle", "Continuar con Google")}
                        />
                        <div className="relative text-center text-sm">
                          <span className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </span>
                          <span className="relative bg-background px-2 text-muted-foreground">
                            {t("auth.login.orDivider", "o")}
                          </span>
                        </div>

                        <form
                          onSubmit={form.handleSubmit(handleSendCode)}
                          className="flex flex-col gap-6"
                        >
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="grid gap-2">
                              <Label htmlFor="firstName">{t("auth.register.firstName")}</Label>
                              <Input
                                id="firstName"
                                type="text"
                                autoComplete="given-name"
                                placeholder={t("auth.register.firstNamePlaceholder")}
                                {...form.register("firstName")}
                              />
                              {form.formState.errors.firstName ? (
                                <p className="text-sm text-destructive">
                                  {form.formState.errors.firstName.message}
                                </p>
                              ) : null}
                            </div>
                            <div className="grid gap-2">
                              <Label htmlFor="lastName">{t("auth.register.lastName")}</Label>
                              <Input
                                id="lastName"
                                type="text"
                                autoComplete="family-name"
                                placeholder={t("auth.register.lastNamePlaceholder")}
                                {...form.register("lastName")}
                              />
                              {form.formState.errors.lastName ? (
                                <p className="text-sm text-destructive">
                                  {form.formState.errors.lastName.message}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="email">{t("auth.register.email")}</Label>
                            <Input
                              id="email"
                              type="email"
                              placeholder="you@example.com"
                              autoComplete="email"
                              {...form.register("email")}
                            />
                            {form.formState.errors.email ? (
                              <p className="text-sm text-destructive">
                                {form.formState.errors.email.message}
                              </p>
                            ) : null}
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="password">{t("auth.register.password")}</Label>
                            <PasswordInput
                              id="password"
                              autoComplete="new-password"
                              placeholder="••••••••"
                              {...form.register("password")}
                            />
                            <PasswordStrengthIndicator
                              password={form.watch("password") ?? ""}
                              showRules
                              rulesLabels={{
                                minLength: t("auth.register.passwordStrength.minLength"),
                                uppercase: t("auth.register.passwordStrength.uppercase"),
                                number: t("auth.register.passwordStrength.number"),
                                special: t("auth.register.passwordStrength.special"),
                              }}
                              strengthLabels={{
                                weak: t("auth.register.passwordStrength.weak"),
                                fair: t("auth.register.passwordStrength.fair"),
                                good: t("auth.register.passwordStrength.good"),
                                strong: t("auth.register.passwordStrength.strong"),
                              }}
                            />
                            {form.formState.errors.password ? (
                              <p className="text-sm text-destructive">
                                {form.formState.errors.password.message}
                              </p>
                            ) : null}
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="confirmPassword">
                              {t("auth.register.confirmPassword")}
                            </Label>
                            <PasswordInput
                              id="confirmPassword"
                              autoComplete="new-password"
                              placeholder="••••••••"
                              {...form.register("confirmPassword")}
                            />
                            {form.formState.errors.confirmPassword ? (
                              <p className="text-sm text-destructive">
                                {form.formState.errors.confirmPassword.message}
                              </p>
                            ) : null}
                          </div>
                          <Button type="submit" disabled={sendingCode} className="w-full">
                            {sendingCode ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t("auth.register.sendingCode")}
                              </>
                            ) : (
                              t("auth.register.nextStepButton")
                            )}
                          </Button>
                        </form>
                      </>
                    ) : (
                      <>
                        <p className="text-muted-foreground text-sm">
                          <Trans
                            i18nKey="auth.register.emailSentMessage"
                            values={{ email }}
                            components={{
                              strong: (
                                <strong className="font-semibold text-foreground" />
                              ),
                            }}
                          />
                        </p>
                        <div className="grid gap-2">
                          <Label htmlFor="code">
                            {t("auth.register.verificationCodeLabel")}
                          </Label>
                          <Input
                            id="code"
                            type="text"
                            inputMode="numeric"
                            placeholder="000000"
                            maxLength={6}
                            value={verificationCode}
                            onChange={(e) => {
                              setVerificationCode(
                                e.target.value.replace(/\D/g, "").slice(0, 6),
                              );
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => void handleResendCode()}
                            disabled={resendCooldown > 0 || resendingCode}
                            className="self-start text-sm text-muted-foreground underline underline-offset-2 hover:text-foreground disabled:no-underline disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {resendingCode
                              ? t("auth.register.resendingCode")
                              : resendCooldown > 0
                                ? t("auth.register.resendCodeIn", {
                                    seconds: resendCooldown,
                                  })
                                : t("auth.register.resendCode")}
                          </button>
                        </div>
                        {turnstileEnabled ? (
                          <div className="grid gap-2">
                            <p className="text-muted-foreground text-sm">
                              {t("auth.register.captchaLabel")}
                            </p>
                            <div className="flex justify-center">
                              <Turnstile
                                ref={turnstileRef}
                                siteKey={TURNSTILE_SITE_KEY}
                                onSuccess={handleTurnstileSuccess}
                                onExpire={handleTurnstileExpire}
                                options={{
                                  language: i18n.language.startsWith("es") ? "es" : "en",
                                  theme: "light",
                                }}
                              />
                            </div>
                          </div>
                        ) : null}
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                              setStep(1);
                              resetTurnstile();
                            }}
                          >
                            {t("pages.common.back")}
                          </Button>
                          <Button
                            type="button"
                            className="flex-1"
                            onClick={handleVerifyAndRegister}
                            disabled={
                              verifying ||
                              (turnstileEnabled &&
                                (turnstileToken == null || turnstileToken === ""))
                            }
                          >
                            {verifying ? (
                              <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t("auth.register.submitting")}
                              </>
                            ) : (
                              t("auth.register.verifyButton")
                            )}
                          </Button>
                        </div>
                      </>
                    )}

                    <div className="text-center">
                      <Link
                        to="/login"
                        className="text-sm text-muted-foreground underline hover:text-foreground"
                      >
                        {t("auth.register.backToLogin")}
                      </Link>
                    </div>
      </div>
    </AuthShell>
  );
}

interface GoogleSsoButtonProps {
  loading: boolean;
  onClick: () => void;
  label: string;
}

function GoogleSsoButton({ loading, onClick, label }: GoogleSsoButtonProps): JSX.Element {
  return (
    <button
      type="button"
      disabled={loading}
      onClick={onClick}
      className="inline-flex w-full items-center justify-center gap-2 rounded-md border bg-background px-4 py-2 text-sm font-medium shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:pointer-events-none disabled:opacity-50"
    >
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      ) : (
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 0 0 1 12c0 1.77.42 3.45 1.18 4.93l3.66-2.84z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
      )}
      {label}
    </button>
  );
}
